import React, { useState, useRef, useEffect } from "react";
import { FaCalendarAlt, FaArrowLeft, FaChevronDown } from "react-icons/fa";
import "../assets/css/AddUser.css";
import "../assets/css/DigixLeads.css";
import axiosInstance from "../components/api/axiosInstance";
import GoogleAddressSearch from "../components/GoogleAddressSearch";
import { getApiUrl } from "../config/apiConfig";
import { PASSWORD_POLICY_HINT, validatePasswordStrength } from "../utils/passwordPolicy";
import { DEFAULT_COUNTRY_CODE, formValueWithMobileSanitize, mobileValidationMessage } from "../utils/phone";
import CountryCodeSelect from "../components/CountryCodeSelect";
import TenantCompanySelect from "../components/TenantCompanySelect";
import OwnerPasswordField from "../components/OwnerPasswordField";
import { getStaffLoginUrl } from "../config/ctxPath";
import { aadhaarError, applyPascalNameFields, personalEmailError, roleDisplayName } from "../utils/formatters";
import ReportingLineFields from "./work/ReportingLineFields";
import DepartmentSelect from "./work/DepartmentSelect";
import { staffWorkApi } from "./work/workApi";

const LEAVING_REQUIRED_STATUSES = ["Resigned", "Terminated"];

const DEFAULT_ROLES = [
  { role_id: 1, role_name: "Admin" },
  { role_id: 2, role_name: "Manager" },
  { role_id: 3, role_name: "Developer" },
  { role_id: 4, role_name: "Tester" },
  { role_id: 5, role_name: "Business Analyst" },
  { role_id: 6, role_name: "Tenant" },
  { role_id: 7, role_name: "HR" },
  { role_id: 8, role_name: "Devops Engineer" },
  { role_id: 9, role_name: "Relationship Manager" },
];

function normalizeRolesFromApi(data) {
  const raw = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.roles)
        ? data.roles
        : [];
  return raw
    .map((r) => ({
      role_id: r.role_id ?? r.roleId ?? r.Designation_Id ?? r.designation_id ?? r.id ?? 0,
      role_name: (
        r.role_name ??
        r.roleName ??
        r.Designation_Name ??
        r.designation_name ??
        r.name ??
        r.role ??
        ""
      ).toString().trim() || "—"
    }))
    .filter((r) => r.role_id != null && r.role_id !== "");
}

function normalizeDesignationsFromApi(data) {
  const raw = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : [];
  return raw
    .map((d) => ({
      designation_id:
        d.Designation_Id ?? d.designation_id ?? d.id ?? 0,
      designation_name:
        (d.Designation_Name ?? d.designation_name ?? d.name ?? "").toString().trim() || "—"
    }))
    .filter((d) => d.designation_id != null && d.designation_id !== "");
}

function AddUser({ onBack, onAddUser }) {
  const reportingRef = useRef(null);
  const dateOfBirthRef = useRef(null);
  const dateOfJoiningRef = useRef(null);
  const dateOfLeavingRef = useRef(null);

  const [rolesList, setRolesList] = useState(DEFAULT_ROLES);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    gender: "",
    countryCode: DEFAULT_COUNTRY_CODE,
    phoneNumber: "",
    altCountryCode: DEFAULT_COUNTRY_CODE,
    altNumber: "",
    aadhaar: "",
    pan: "",
    dateOfBirth: "",
    addressLine1: "",
    addressLine2: "",
    area: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    formattedAddress: "",
    company: "",
    companyEmail: "",
    employeeId: "",
    companyClientId: "",
    // Multi-role selection
    role: "",
    roleIds: [],
    designationId: "",
    department: "",
    band: "",
    dateOfJoining: "",
    dateOfLeaving: "",
    status: "Active",
    companyLocation: "",
    profileImage: null,
    password: ""
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [designationDropdownOpen, setDesignationDropdownOpen] = useState(false);
  const [designationSearchQuery, setDesignationSearchQuery] = useState("");
  const designationDropdownRef = useRef(null);

  const genders = [
    "Select Gender",
    "Male",
    "Female",
    "Other",
    "Prefer not to say"
  ];

  const bands = [
    "Select Band",
    "A",
    "B",
    "C",
    "D",
    "E",
    "Not Assigned"
  ];

  const statuses = [
    "Select Status",
    "Active",
    "Inactive",
    "Resigned",
    "Terminated",
    "On Hold"
  ];

  const [designationsList, setDesignationsList] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rolesResponse, designationsResponse] = await Promise.all([
          axiosInstance.get("/roles"),
          axiosInstance.get("/designations")
        ]);

        const roles = normalizeRolesFromApi(rolesResponse?.data);
        if (roles.length > 0) {
          setRolesList(roles);
        }

        const designations = normalizeDesignationsFromApi(designationsResponse?.data);
        if (designations.length > 0) {
          setDesignationsList(designations);
        }
      } catch (err) {
        console.error(
          "Error fetching roles/designations:",
          err.response?.data?.message || err.message || err
        );
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        designationDropdownRef.current &&
        !designationDropdownRef.current.contains(e.target)
      ) {
        setDesignationDropdownOpen(false);
        setDesignationSearchQuery("");
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === "file") {
      setFormData(prev => ({
        ...prev,
        [name]: files[0] || null
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: formValueWithMobileSanitize(name, value)
      }));
    }
  };

  const updateRoleSelection = (newRoleIds) => {
    const uniqueIds = Array.from(new Set(newRoleIds));
    const selectedRoles = rolesList.filter((r) =>
      uniqueIds.includes(String(r.role_id))
    );
    const roleLabel =
      selectedRoles.length === 0
        ? ""
        : selectedRoles.length === 1
          ? selectedRoles[0].role_name
          : `${selectedRoles.length} roles selected`;

    setFormData((prev) => ({
      ...prev,
      roleIds: uniqueIds,
      role: roleLabel
    }));
  };

  const handleToggleRole = (roleId) => {
    const idStr = String(roleId);
    setFormData((prev) => {
      const current = Array.isArray(prev.roleIds) ? prev.roleIds : [];
      const exists = current.includes(idStr);
      const next = exists ? current.filter((id) => id !== idStr) : [...current, idStr];
      const selectedRoles = rolesList.filter((r) => next.includes(String(r.role_id)));
      const roleLabel =
        selectedRoles.length === 0
          ? ""
          : selectedRoles.length === 1
            ? selectedRoles[0].role_name
            : `${selectedRoles.length} roles selected`;
      return {
        ...prev,
        roleIds: next,
        role: roleLabel
      };
    });
  };

  const handleToggleSelectAllRoles = () => {
    if (!rolesList.length) return;
    const allIds = rolesList.map((r) => String(r.role_id));
    const current = Array.isArray(formData.roleIds) ? formData.roleIds : [];
    if (current.length === rolesList.length) {
      updateRoleSelection([]);
    } else {
      updateRoleSelection(allIds);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validate required fields for API
    if (!formData.firstName || !formData.lastName || !formData.email || 
        !formData.companyEmail || !formData.password ||
        !Array.isArray(formData.roleIds) || formData.roleIds.length === 0 ||
        !formData.designationId ||
        !formData.department || !formData.status) {
      alert("Please fill in all required fields (*)");
      return;
    }

    const fieldIssue = personalEmailError(formData.email, formData.companyEmail) || aadhaarError(formData.aadhaar);
    if (fieldIssue) {
      setError(fieldIssue);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (!formData.addressLine1 && !formData.city && !formData.formattedAddress) {
      alert("Please select an address from Google Maps search.");
      return;
    }

    if (LEAVING_REQUIRED_STATUSES.includes(formData.status) && !formData.dateOfLeaving) {
      alert(`Date of Leaving is required when status is ${formData.status}.`);
      return;
    }

    const passwordError = validatePasswordStrength(formData.password);
    if (passwordError) {
      alert(passwordError);
      return;
    }
    const phoneError = mobileValidationMessage(formData.phoneNumber, { label: "Phone number" });
    if (phoneError) {
      alert(phoneError);
      return;
    }
    const altError = mobileValidationMessage(formData.altNumber, { label: "Alternate phone" });
    if (altError) {
      alert(altError);
      return;
    }

    setLoading(true);
    try {
      // API expects JSON: first_name, last_name, email, company_email, password,
      // role_ids (array), department, status, designation_id, plus optional fields.
      const selectedRoleIds = (Array.isArray(formData.roleIds) ? formData.roleIds : [])
        .map((id) => parseInt(id, 10))
        .filter((n) => !Number.isNaN(n));
      const primaryRoleId = selectedRoleIds[0];
      const parsedDesignationId = parseInt(formData.designationId, 10);

      const payload = applyPascalNameFields({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        company_email: formData.companyEmail,
        password: formData.password,
        // Role and designation
        role_id: primaryRoleId,
        role_ids: selectedRoleIds.length ? selectedRoleIds : undefined,
        designation_id: !Number.isNaN(parsedDesignationId) ? parsedDesignationId : undefined,
        // Optional fields
        department: formData.department || undefined,
        status: formData.status || undefined,
        gender: formData.gender || undefined,
        country_code: formData.phoneNumber ? formData.countryCode : undefined,
        phone_number: formData.phoneNumber || undefined,
        alt_number: formData.altNumber || undefined,
        aadhar: formData.aadhaar || undefined,
        pan: formData.pan || undefined,
        dob: formData.dateOfBirth || undefined,
        address_line_1: formData.addressLine1 || undefined,
        address_line_2: formData.addressLine2 || undefined,
        area: formData.area || undefined,
        city: formData.city || undefined,
        state: formData.state || undefined,
        country: formData.country || undefined,
        pincode: formData.pincode || undefined,
        company_name: formData.company || undefined,
        company_client_id: formData.companyClientId || undefined,
        band: formData.band || undefined,
        doj: formData.dateOfJoining || undefined,
        date_of_joining: formData.dateOfJoining || undefined,
        date_of_leaving: formData.dateOfLeaving || undefined,
        company_location: formData.companyLocation || undefined
        // Note: profileImage is not sent here; create-user currently expects JSON fields only.
      });

      const response = await axiosInstance.post("/create-user", payload);
      
      if (response.data) {
        // Build user from API response + form so table shows all fields after refetch
        const d = response.data;
        const API_HOST = getApiUrl();
        let imageUrl = d.image_url || d.image_path || "";
        if (imageUrl && typeof imageUrl === "string") {
          const pathMatch = imageUrl.match(/\/uploads\/[^?]*/);
          if (pathMatch) {
            imageUrl = `${API_HOST.replace(/\/$/, "")}${pathMatch[0]}`;
          } else if (imageUrl.includes("localhost") || /^\d+\.\d+\.\d+\.\d+/.test(imageUrl)) {
            try {
              const u = new URL(imageUrl);
              imageUrl = `${API_HOST.replace(/\/$/, "")}${u.pathname}${u.search}`;
            } catch (_) {}
          }
        }
        // Normalize roles from response (if provided)
        const rawRoles = Array.isArray(d.roles) ? d.roles : [];
        const normalizedRoles = rawRoles
          .map((r) => ({
            roleId: r.role_id ?? r.roleId ?? r.id ?? null,
            roleName: r.role_name ?? r.roleName ?? r.name ?? r.role ?? ""
          }))
          .filter((r) => r.roleId != null || (r.roleName || "").trim() !== "");
        const primaryRoleName =
          (normalizedRoles[0]?.roleName || "").trim() ||
          d.role_name ||
          formData.role ||
          "";

        const newUser = {
          id: d.user_id,
          userId: d.user_id,
          firstName: d.first_name ?? formData.firstName,
          lastName: d.last_name ?? formData.lastName,
          email: d.email ?? formData.email,
          companyEmail: d.company_email ?? formData.companyEmail,
          role: primaryRoleName,
          roles: normalizedRoles,
          roleId: d.role_id ?? primaryRoleId ?? null,
          designation: (d.designation_name ?? formData.designationId) || "",
          department: d.department ?? formData.department,
          status: d.status ?? formData.status,
          band: d.band ?? formData.band,
          employeeId: d.employee_id ?? formData.employeeId,
          phoneNumber: d.phone_number ?? formData.phoneNumber,
          altNumber: d.alt_number ?? formData.altNumber,
          aadhaar: d.aadhar ?? formData.aadhaar,
          pan: d.pan ?? formData.pan,
          addressLine1: d.address_line_1 ?? formData.addressLine1,
          addressLine2: d.address_line_2 ?? formData.addressLine2,
          area: d.area ?? formData.area,
          city: d.city ?? formData.city,
          state: d.state ?? formData.state,
          country: d.country ?? formData.country,
          pincode: d.pincode ?? formData.pincode,
          dateOfJoining: d.doj ?? d.date_of_joining ?? formData.dateOfJoining,
          dateOfLeaving: d.date_of_leaving ?? formData.dateOfLeaving,
          createdOn: d.created_on,
          updatedOn: d.updated_on,
          image_url: imageUrl || undefined,
          image_path: d.image_path
        };
        if (onAddUser) {
          onAddUser(newUser);
        }

        const reportingError = d.user_id ? await reportingRef.current?.save(d.user_id) : null;
        alert(reportingError ? `User created, but the reporting line was not saved: ${reportingError}` : "User created successfully!");
        
        // Navigate back to users page
        if (onBack) {
          onBack();
        }
      }
    } catch (error) {
      console.error("Error creating user:", error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          "Failed to create user. Please try again.";
      setError(errorMessage);
      alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="adduser-container">
      <button className="back-link" onClick={onBack}>
        <FaArrowLeft /> Back
      </button>
      <form className="adduser-form" onSubmit={handleSubmit}>
        {error && <div className="error-message" style={{ 
          backgroundColor: '#fee', 
          color: '#c33', 
          padding: '12px', 
          borderRadius: '4px', 
          marginBottom: '20px',
          fontSize: '14px'
        }}>{error}</div>}
        
        {/* Personal Information */}
        <div className="form-section">
          <h2 className="section-title">Personal Information</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>First Name <span className="required">*</span></label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name <span className="required">*</span></label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Gender <span className="required">*</span></label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                required
              >
                {genders.map(gender => (
                  <option key={gender} value={gender === "Select Gender" ? "" : gender}>
                    {gender}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Aadhaar Number <span className="required">*</span></label>
              <input
                type="text"
                name="aadhaar"
                value={formData.aadhaar}
                onChange={handleInputChange}
                inputMode="numeric"
                maxLength={14}
                placeholder="xxxx xxxx xxxx"
                required
              />
              {formData.aadhaar && aadhaarError(formData.aadhaar) ? (
                <span className="adduser-field-error">{aadhaarError(formData.aadhaar)}</span>
              ) : null}
            </div>
            <div className="form-group">
              <label>PAN <span className="required">*</span></label>
              <input
                type="text"
                name="pan"
                value={formData.pan}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Date of Birth <span className="required">*</span></label>
              <div className="date-input-wrapper">
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  ref={dateOfBirthRef}
                  required
                />
                <FaCalendarAlt 
                  className="calendar-icon" 
                  onClick={() => dateOfBirthRef.current?.showPicker()}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Personal Phone <span className="required">*</span></label>
              <div className="adduser-phone-row">
                <CountryCodeSelect
                  value={formData.countryCode}
                  onChange={(code) => setFormData((prev) => ({ ...prev, countryCode: code }))}
                  ariaLabel="Personal phone country code"
                />
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  inputMode="numeric"
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Alt Phone <span className="required">*</span></label>
              <div className="adduser-phone-row">
                <CountryCodeSelect
                  value={formData.altCountryCode}
                  onChange={(code) => setFormData((prev) => ({ ...prev, altCountryCode: code }))}
                  ariaLabel="Alt phone country code"
                />
                <input
                  type="tel"
                  name="altNumber"
                  value={formData.altNumber}
                  onChange={handleInputChange}
                  inputMode="numeric"
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Personal Email <span className="required">*</span></label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
              {personalEmailError(formData.email, formData.companyEmail) ? (
                <span className="adduser-field-error">
                  {personalEmailError(formData.email, formData.companyEmail)}
                </span>
              ) : null}
            </div>
            <GoogleAddressSearch
              required
              value={{
                addressLine1: formData.addressLine1,
                addressLine2: formData.addressLine2,
                area: formData.area,
                city: formData.city,
                state: formData.state,
                country: formData.country,
                pincode: formData.pincode,
                formattedAddress: formData.formattedAddress,
              }}
              onChange={(next) =>
                setFormData((prev) => ({
                  ...prev,
                  addressLine1: next.addressLine1 || "",
                  addressLine2: next.addressLine2 || "",
                  area: next.area || "",
                  city: next.city || "",
                  state: next.state || "",
                  country: next.country || "",
                  pincode: next.pincode || "",
                  formattedAddress: next.formattedAddress || "",
                }))
              }
            />
          </div>
        </div>

        {/* Professional Information */}
        <div className="form-section">
          <h2 className="section-title">Professional Information</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Company <span className="required">*</span></label>
              <TenantCompanySelect
                value={formData.company}
                onChange={handleInputChange}
                onTenantChange={(id) => setFormData((prev) => ({ ...prev, companyClientId: id }))}
                required
              />
            </div>
            <div className="form-group">
              <label>Company Email <span className="required">*</span></label>
              <input
                type="email"
                name="companyEmail"
                value={formData.companyEmail}
                onChange={handleInputChange}
                required
              />
            </div>
            <OwnerPasswordField
              label="Password"
              required
              value={formData.password}
              onChange={(password) => setFormData((prev) => ({ ...prev, password }))}
              inlineShare
              hint={PASSWORD_POLICY_HINT}
              loginEmail={String(formData.companyEmail || "").trim()}
              portalUrl={`${window.location.origin}${getStaffLoginUrl()}`}
              recipientName={String(formData.firstName || "").trim()}
              recipientEmail={String(formData.email || "").trim()}
              recipientPhone={formData.phoneNumber ? `${formData.countryCode}${formData.phoneNumber}` : ""}
              saveNote="Save the employee first so this password is active."
              missingNote="Enter the Company Email and a password to share the login."
            />
            <div className="form-group">
              <label>Employee ID</label>
              <input
                type="text"
                name="employeeId"
                value=""
                placeholder="Auto-generated on save"
                readOnly
                disabled
              />
            </div>
            <div className="form-group role-multiselect-group">
              <label>Role <span className="required">*</span></label>
              <div
                className={`multi-select-trigger ${isRoleDropdownOpen ? "open" : ""}`}
                onClick={() => setIsRoleDropdownOpen((open) => !open)}
              >
                <span className={formData.role ? "" : "placeholder-text-dark"}>
                  {roleDisplayName(formData.role) || "Select Role"}
                </span>
                <FaChevronDown className="multi-select-arrow" />
              </div>
              {isRoleDropdownOpen && (
                <div className="multi-select-panel">
                  <label className="multi-select-item">
                    <input
                      type="checkbox"
                      checked={
                        Array.isArray(formData.roleIds) &&
                        formData.roleIds.length === rolesList.length &&
                        rolesList.length > 0
                      }
                      onChange={handleToggleSelectAllRoles}
                    />
                    <span>Select all</span>
                  </label>
                  {rolesList.map((r) => {
                    const idStr = String(r.role_id);
                    const checked =
                      Array.isArray(formData.roleIds) &&
                      formData.roleIds.includes(idStr);
                    return (
                      <label key={r.role_id} className="multi-select-item">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleToggleRole(idStr)}
                        />
                        <span>{roleDisplayName(r.role_name)}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          <div className="form-group">
            <label>Designation <span className="required">*</span></label>
            <div className="domain-filter-dropdown" ref={designationDropdownRef} style={{ minWidth: "100%" }}>
              <div
                className="search-filter-input search-filter-select domain-filter-trigger"
                style={{
                  width: "100%",
                  height: 42,
                  borderRadius: 5,
                  border: "1px solid #D6E0EA",
                  padding: "0 12px",
                  fontSize: 14,
                  background: "#fff",
                  boxSizing: "border-box"
                }}
                onClick={() => setDesignationDropdownOpen((o) => !o)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setDesignationDropdownOpen((o) => !o);
                  }
                }}
              >
                <span className={formData.designationId ? "" : "placeholder-text-dark"}>
                  {formData.designationId
                    ? (() => {
                        const sel = designationsList.find(
                          (d) => String(d.designation_id) === String(formData.designationId)
                        );
                        return sel ? sel.designation_name : "Select Designation";
                      })()
                    : "Select Designation"}
                </span>
                <FaChevronDown
                  className={`domain-filter-chevron ${designationDropdownOpen ? "open" : ""}`}
                />
              </div>
              {designationDropdownOpen && (
                <div className="domain-filter-panel">
                  <input
                    type="text"
                    className="domain-filter-search"
                    placeholder="Search designations..."
                    value={designationSearchQuery}
                    onChange={(e) => setDesignationSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                  <div className="domain-filter-options">
                    <div
                      className={`domain-filter-option ${!formData.designationId ? "selected" : ""}`}
                      onClick={() => {
                        setFormData((p) => ({ ...p, designationId: "" }));
                        setDesignationDropdownOpen(false);
                        setDesignationSearchQuery("");
                      }}
                    >
                      Select Designation
                    </div>
                    {designationsList
                      .filter((d) => {
                        const q = designationSearchQuery.trim().toLowerCase();
                        if (!q) return true;
                        return String(d.designation_name || "")
                          .toLowerCase()
                          .includes(q);
                      })
                      .map((d) => (
                        <div
                          key={d.designation_id}
                          className={`domain-filter-option ${
                            String(formData.designationId) === String(d.designation_id) ? "selected" : ""
                          }`}
                          onClick={() => {
                            setFormData((p) => ({
                              ...p,
                              designationId: String(d.designation_id)
                            }));
                            setDesignationDropdownOpen(false);
                            setDesignationSearchQuery("");
                          }}
                        >
                          {d.designation_name}
                        </div>
                      ))}
                    {designationsList.filter((d) => {
                      const q = designationSearchQuery.trim().toLowerCase();
                      if (!q) return true;
                      return String(d.designation_name || "")
                        .toLowerCase()
                        .includes(q);
                    }).length === 0 && (
                      <div className="domain-filter-no-results">No designations found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
            <div className="form-group">
              <label htmlFor="emp-department">Department <span className="required">*</span></label>
              <DepartmentSelect
                id="emp-department"
                api={staffWorkApi}
                value={formData.department}
                onChange={(name) => setFormData((p) => ({ ...p, department: name }))}
                required
              />
            </div>
            <ReportingLineFields ref={reportingRef} api={staffWorkApi} />
            <div className="form-group">
              <label>Band <span className="required">*</span></label>
              <select
                name="band"
                value={formData.band}
                onChange={handleInputChange}
                required
              >
                {bands.map(band => (
                  <option key={band} value={band === "Select Band" ? "" : band}>
                    {band}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Company Location <span className="required">*</span></label>
              <input
                type="text"
                name="companyLocation"
                value={formData.companyLocation}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Date of Joining <span className="required">*</span></label>
              <div className="date-input-wrapper">
                <input
                  type="date"
                  name="dateOfJoining"
                  value={formData.dateOfJoining}
                  onChange={handleInputChange}
                  ref={dateOfJoiningRef}
                  required
                />
                <FaCalendarAlt 
                  className="calendar-icon" 
                  onClick={() => dateOfJoiningRef.current?.showPicker()}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Status <span className="required">*</span></label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                required
              >
                {statuses.map(status => (
                  <option key={status} value={status === "Select Status" ? "" : status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                Date of Leaving
                {LEAVING_REQUIRED_STATUSES.includes(formData.status) ? (
                  <span className="required"> *</span>
                ) : null}
              </label>
              <div className="date-input-wrapper">
                <input
                  type="date"
                  name="dateOfLeaving"
                  value={formData.dateOfLeaving}
                  onChange={handleInputChange}
                  ref={dateOfLeavingRef}
                  required={LEAVING_REQUIRED_STATUSES.includes(formData.status)}
                />
                <FaCalendarAlt 
                  className="calendar-icon" 
                  onClick={() => dateOfLeavingRef.current?.showPicker()}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Profile Image</label>
              <div className="file-input-wrapper">
                <input
                  type="file"
                  name="profileImage"
                  id="profileImage"
                  accept="image/*"
                  onChange={handleInputChange}
                  style={{ display: "none" }}
                />
                <label htmlFor="profileImage" className="file-input-label">
                  Choose File
                </label>
                <span className="file-name">
                  {formData.profileImage ? formData.profileImage.name : "No file chosen"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="adduser-form-actions">
          <button type="submit" className="btn-register" disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddUser;
